import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { fromCents, toCents, errorWords } from "@alm/domain";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { useFacilityToday } from "@/hooks/use-facility-today";
import { useFacility } from "@/lib/facility/facility";
import { invalidateKeys } from "@/lib/query/invalidate-keys";
import { orpc } from "@/lib/orpc";
import { optionalMonth, optionalTrue, stringParams, unlessAll, KEEP_PLACE } from "@/lib/search";
import { BulkEntryDialog } from "./-components/bulk-entry-dialog";
import { rowFromDraft, type BulkRow } from "./-components/bulk-rows";
import { ExpenseTable } from "./-components/expense-table";
import { RecordExpenseDialog } from "./-components/record-expense-dialog";
import { ExpenseFilters } from "./-components/expense-filters";
import { stageOf } from "./-components/payment-stage";
import { type PaymentStage } from "@/routes/_authenticated/_financials/expense-entries/-components/payment-stage-types";
import { useCan, useCanChangeRow } from "@/lib/policy";

type ExpenseSearch = {
  period?: string;
  category?: string;
  vendor?: string;
  stage?: string;
  tag?: string;
  voided?: true;
};

export const Route = createFileRoute("/_authenticated/_financials/expense-entries/")({
  validateSearch: (search: Record<string, unknown>): ExpenseSearch => ({
    period: optionalMonth(search.period),
    ...stringParams(["category", "vendor", "stage", "tag"], search),
    voided: optionalTrue(search.voided),
  }),
  component: Expenses,
});

const fail = (error: Error) => toast.error(errorWords(error));

/** What the building spent in a month, and what it was booked to. */
function Expenses() {
  const { facilityId } = useFacility();
  const today = useFacilityToday(facilityId);
  const canWrite = useCan("financial:manage");
  const canPlace = useCanChangeRow("financial:manage");
  const queryClient = useQueryClient();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<ExpenseSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const chosen = params.period ?? today.slice(0, 7);
  const year = Number(chosen.slice(0, 4));
  const month = Number(chosen.slice(5, 7));
  const [adding, setAdding] = useState(false);
  const [bulk, setBulk] = useState<BulkRow[] | null>(null);
  const categoryFilter = params.category ?? "all";
  const vendorFilter = params.vendor ?? "all";
  const includeVoided = Boolean(params.voided);
  const stageFilter = (params.stage ?? "all") as PaymentStage | "all";
  const tagFilter = params.tag ?? "all";
  const [voiding, setVoiding] = useState<{ id: string; label: string } | null>(null);

  const entries = useQuery(
    orpc.financials.expenseEntries.list.queryOptions({
      input: {
        facilityId,
        year,
        month,
        includeVoided,
        ...(categoryFilter === "all" ? {} : { categoryId: categoryFilter }),
        ...(vendorFilter === "all" ? {} : { vendorId: vendorFilter }),
      },
    }),
  );
  const summary = useQuery(
    orpc.financials.expenseEntries.summary.queryOptions({ input: { facilityId, year, month } }),
  );
  const categories = useQuery(orpc.financials.expenseCategories.list.queryOptions({}));
  const tags = useQuery(orpc.financials.expenseTags.list.queryOptions({}));
  const vendors = useQuery(orpc.financials.vendors.list.queryOptions({ input: { facilityId } }));

  const voidEntry = useMutation(
    orpc.financials.expenseEntries.void.mutationOptions({
      onSuccess: async () => {
        toast.success("Entry voided. It stays on the record.");
        await invalidateKeys(queryClient, [orpc.financials.key()]);
      },
      onError: fail,
    }),
  );
  /* The one thing that moves a bill along. The AP report counts these states;
     without this the whole building sat in "created" for ever. */
  const advance = useMutation(
    orpc.financials.expenseEntries.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Payment stage updated");
        await invalidateKeys(queryClient, [orpc.financials.key()]);
      },
      onError: fail,
    }),
  );
  const mirror = useMutation(
    orpc.financials.expenseEntries.mirrorMonth.mutationOptions({
      // The drafts open for booking; before, they were counted in a toast and dropped.
      onSuccess: (drafts) => {
        // A cost booked to the whole organization is copied only by its own bookkeepers.
        const mine = drafts.filter((draft) => canPlace(draft.facilityId ?? null));
        if (mine.length < drafts.length) {
          toast.info("Costs booked to the whole organization were left for its bookkeepers");
        }
        if (mine.length === 0) {
          if (drafts.length === 0) toast.info("Nothing was booked this month to copy forward");
          return;
        }
        setBulk(mine.map(rowFromDraft));
      },
      onError: fail,
    }),
  );

  const period = `${year}-${String(month).padStart(2, "0")}`;
  const rows = (entries.data ?? []).filter((entry) => {
    if (stageFilter !== "all" && stageOf(entry) !== stageFilter) return false;
    if (tagFilter !== "all" && !entry.tags.some((tag) => tag.id === tagFilter)) return false;
    return true;
  });
  /* The figure somebody reconciles against a bank statement, over the rows
     they are actually looking at rather than over the unfiltered month. */
  const shownTotal = rows
    .filter((entry) => !entry.isVoided)
    .reduce((sum, entry) => sum + toCents(entry.amount), 0);
  // "Show voided" only widens the list, so it never explains an empty one.
  const filtered = [categoryFilter, vendorFilter, stageFilter, tagFilter].some(
    (value) => value !== "all",
  );

  return (
    <>
      <PageHeader
        title="Expense entries"
        meta="What the building spent, by month and by category."
        actions={
          canWrite ? (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setBulk([])}>
                Enter several
              </Button>
              <Button onClick={() => setAdding(true)}>Record an expense</Button>
            </div>
          ) : null
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Label className="text-muted-foreground" htmlFor="expenses-period">
          Month
        </Label>
        <Input
          id="expenses-period"
          type="month"
          value={period}
          onChange={(event) => {
            const [nextYear, nextMonth] = event.target.value.split("-");
            if (nextYear && nextMonth) {
              const next = `${nextYear}-${nextMonth}`;
              patch({ period: next === today.slice(0, 7) ? undefined : next });
            }
          }}
          className="w-40 pointer-coarse:w-48"
        />
        {canWrite ? (
          <Button
            variant="outline"
            size="sm"
            disabled={mirror.isPending}
            onClick={() =>
              mirror.mutate({
                sourceFacilityId: facilityId === "all" ? null : facilityId,
                sourceYear: year,
                sourceMonth: month,
                targetYear: month === 12 ? year + 1 : year,
                targetMonth: month === 12 ? 1 : month + 1,
              })
            }
          >
            Copy this month forward
          </Button>
        ) : null}

        <div className="sm:ms-auto">
          <ExpenseFilters
            value={{
              category: categoryFilter,
              vendor: vendorFilter,
              stage: stageFilter,
              tag: tagFilter,
              voided: includeVoided,
            }}
            onChange={(change) => {
              const next: Partial<ExpenseSearch> = {};
              if (change.category !== undefined) next.category = unlessAll(change.category);
              if (change.vendor !== undefined) next.vendor = unlessAll(change.vendor);
              if (change.stage !== undefined) next.stage = unlessAll(change.stage);
              if (change.tag !== undefined) next.tag = unlessAll(change.tag);
              if (change.voided !== undefined) next.voided = change.voided ? true : undefined;
              patch(next);
            }}
            categories={categories.data ?? []}
            vendors={vendors.data ?? []}
            tags={tags.data ?? []}
          />
        </div>
      </div>

      {summary.isPending ? (
        <Skeleton className="h-20 w-full" />
      ) : (
        <StatTiles>
          <StatTile label="Total" value={summary.data?.total ?? "$0.00"} />
          {summary.data?.byCategory.slice(0, 3).map((row) => (
            <StatTile key={row.category} label={row.category} value={row.total} />
          ))}
        </StatTiles>
      )}

      {entries.isError ? (
        // A failed read is not a month with nothing spent.
        <ReadFailed
          message="Expense entries could not be loaded."
          error={entries.error}
          onRetry={() => void entries.refetch()}
          retrying={entries.isFetching}
        />
      ) : entries.isPending ? (
        <Skeleton className="h-64 w-full" />
      ) : rows.length > 0 ? (
        <ExpenseTable
          rows={rows}
          canWrite={canWrite}
          advancing={advance.isPending}
          onAdvance={(entryId, field) => advance.mutate({ entryId, [field]: today })}
          categories={categories.data ?? []}
          vendors={vendors.data ?? []}
          onVoid={setVoiding}
          total={fromCents(shownTotal)}
        />
      ) : filtered ? (
        <EmptyState
          title="No entries match these filters"
          description={`Nothing in ${period} matches the filters chosen.`}
          action={
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                patch({
                  category: undefined,
                  vendor: undefined,
                  stage: undefined,
                  tag: undefined,
                  voided: undefined,
                });
              }}
            >
              Clear filters
            </Button>
          }
        />
      ) : (
        <EmptyState
          title="Nothing recorded for this month"
          description="Expenses are booked to a category so the P&L adds up across buildings."
        />
      )}

      {voiding ? (
        <RemoveConfirm
          title="Void this entry?"
          confirmLabel="Void entry"
          description={`${voiding.label}. It stays on the record and out of the totals, because a month already reported has to keep reconciling.`}
          onConfirm={() => voidEntry.mutate({ entryId: voiding.id })}
          onClose={() => setVoiding(null)}
        />
      ) : null}

      <RecordExpenseDialog
        open={adding}
        onOpenChange={setAdding}
        facilityId={facilityId === "all" ? null : facilityId}
        year={year}
        month={month}
        categories={categories.data ?? []}
        vendors={vendors.data ?? []}
      />
      {bulk ? (
        <BulkEntryDialog
          rows={bulk}
          facilityId={facilityId === "all" ? null : facilityId}
          defaultDate={today}
          categories={categories.data ?? []}
          vendors={vendors.data ?? []}
          onClose={() => setBulk(null)}
        />
      ) : null}
    </>
  );
}
